import {
  BoxGeometry,
  BufferGeometry,
  ConeGeometry,
  CylinderGeometry,
  DodecahedronGeometry,
  Shape,
  ShapeGeometry,
  SphereGeometry,
  TorusGeometry,
} from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

import type {
  CarriedAccessory,
  FacialHair,
  HairStyle,
  Headwear,
  Neckwear,
} from "../../sim/appearance.ts";

type VisibleHairStyle = Exclude<HairStyle, "bald">;
type VisibleFacialHair = Exclude<FacialHair, "none">;
type VisibleHeadwear = Exclude<Headwear, "none">;
type VisibleNeckwear = Exclude<Neckwear, "none">;
type VisibleCarriedAccessory = Exclude<CarriedAccessory, "none">;

export interface CharacterFeatureGeometrySet {
  readonly hair: Readonly<
    Record<HairStyle, Readonly<Record<FacialHair, BufferGeometry>>>
  >;
  readonly headwear: Readonly<Record<VisibleHeadwear, BufferGeometry>>;
  readonly neckwear: Readonly<Record<VisibleNeckwear, BufferGeometry>>;
  readonly carried: Readonly<
    Record<VisibleCarriedAccessory, BufferGeometry>
  >;
  readonly eyewear: Readonly<{
    round: BufferGeometry;
    square: BufferGeometry;
  }>;
}

interface Placement {
  readonly x?: number;
  readonly y?: number;
  readonly z?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  readonly scaleZ?: number;
  readonly rotateX?: number;
  readonly rotateY?: number;
  readonly rotateZ?: number;
}

function place(
  geometry: BufferGeometry,
  placement: Readonly<Placement> = {},
): BufferGeometry {
  geometry.scale(
    placement.scaleX ?? 1,
    placement.scaleY ?? 1,
    placement.scaleZ ?? 1,
  );
  geometry.rotateX(placement.rotateX ?? 0);
  geometry.rotateY(placement.rotateY ?? 0);
  geometry.rotateZ(placement.rotateZ ?? 0);
  geometry.translate(
    placement.x ?? 0,
    placement.y ?? 0,
    placement.z ?? 0,
  );
  return geometry;
}

function merge(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const compatible = geometries.map((geometry) =>
    geometry.index === null ? geometry : geometry.toNonIndexed()
  );
  const geometry = mergeGeometries(compatible, false);
  if (geometry === null) {
    throw new Error("Could not merge " + name + " character geometry");
  }
  geometry.name = name;
  for (const source of geometries) {
    source.dispose();
  }
  for (const normalized of compatible) {
    if (!geometries.includes(normalized)) {
      normalized.dispose();
    }
  }
  return geometry;
}

function polygon(
  points: readonly Readonly<{ x: number; y: number }>[],
): ShapeGeometry {
  const [first, ...rest] = points;
  if (first === undefined) {
    throw new Error("Character feature polygon requires at least one point");
  }
  const shape = new Shape();
  shape.moveTo(first.x, first.y);
  for (const point of rest) {
    shape.lineTo(point.x, point.y);
  }
  shape.closePath();
  return new ShapeGeometry(shape);
}

function cap(
  radius: number,
  depthRadians: number,
  name: string,
): BufferGeometry {
  const geometry = new SphereGeometry(
    radius,
    10,
    7,
    0,
    Math.PI * 2,
    0,
    depthRadians,
  );
  geometry.name = name;
  return geometry;
}

function createBaseHairGeometry(
  headRadius: number,
): Readonly<Record<VisibleHairStyle, BufferGeometry>> {
  const buzzCut = place(
    cap(headRadius * 1.018, Math.PI * 0.4, "hair:buzz-cut"),
    { y: headRadius * 0.06, scaleY: 0.94, scaleZ: 1.01 },
  );
  const cropped = place(
    cap(headRadius * 1.025, Math.PI * 0.43, "hair:cropped"),
    { y: headRadius * 0.06, scaleZ: 1.02 },
  );
  const undercut = merge([
    place(cap(headRadius * 1.018, Math.PI * 0.4, "undercut-cap"), {
      y: headRadius * 0.05,
      scaleY: 0.93,
    }),
    place(new DodecahedronGeometry(headRadius * 0.5, 0), {
      x: -headRadius * 0.08,
      y: headRadius * 0.66,
      z: headRadius * 0.08,
      scaleX: 0.8,
      scaleY: 0.5,
      scaleZ: 1.15,
      rotateZ: -0.12,
    }),
  ], "hair:undercut");
  const parted = merge([
    place(cap(headRadius * 1.035, Math.PI * 0.45, "parted-cap"), {
      y: headRadius * 0.08,
      x: headRadius * 0.025,
    }),
    place(new DodecahedronGeometry(headRadius * 0.43, 0), {
      x: -headRadius * 0.46,
      y: headRadius * 0.19,
      z: headRadius * 0.72,
      scaleX: 0.52,
      scaleY: 0.82,
      scaleZ: 0.32,
      rotateZ: 0.24,
    }),
  ], "hair:parted");
  const bob = merge([
    place(cap(headRadius * 1.055, Math.PI * 0.46, "bob-cap"), {
      y: headRadius * 0.09,
      scaleZ: 1.04,
    }),
    ...([-1, 1] as const).map((side) =>
      place(new SphereGeometry(headRadius * 0.52, 8, 5), {
        x: side * headRadius * 0.78,
        y: -headRadius * 0.2,
        z: -headRadius * 0.03,
        scaleX: 0.72,
        scaleY: 1.18,
        scaleZ: 0.8,
      })
    ),
  ], "hair:bob");
  const wavy = merge([
    place(cap(headRadius * 1.045, Math.PI * 0.46, "wavy-cap"), {
      y: headRadius * 0.08,
    }),
    ...([-1, 1] as const).flatMap((side) =>
      [-0.22, 0.15, 0.49].map((height, index) =>
        place(new DodecahedronGeometry(headRadius * 0.34, 0), {
          x: side * headRadius * (0.78 + index * 0.035),
          y: headRadius * height,
          z: headRadius * (0.02 - index * 0.12),
          scaleX: 0.72,
          scaleY: 0.9,
          scaleZ: 0.72,
        })
      )
    ),
  ], "hair:wavy");
  const ponytail = merge([
    place(cap(headRadius * 1.028, Math.PI * 0.45, "ponytail-cap"), {
      y: headRadius * 0.08,
    }),
    place(new DodecahedronGeometry(headRadius * 0.35, 0), {
      y: headRadius * 0.08,
      z: -headRadius * 1.05,
      scaleX: 0.76,
      scaleY: 1.65,
      scaleZ: 0.72,
      rotateX: -0.16,
    }),
  ], "hair:ponytail");
  const bun = merge([
    place(cap(headRadius * 1.025, Math.PI * 0.45, "bun-cap"), {
      y: headRadius * 0.08,
    }),
    place(new DodecahedronGeometry(headRadius * 0.49, 0), {
      y: headRadius * 0.96,
      z: -headRadius * 0.54,
      scaleZ: 0.88,
    }),
  ], "hair:bun");
  return {
    buzz_cut: buzzCut,
    cropped,
    undercut,
    parted,
    bob,
    wavy,
    ponytail,
    bun,
  };
}

function createFacialHairGeometry(
  headRadius: number,
): Readonly<Record<VisibleFacialHair, BufferGeometry>> {
  const moustacheParts = (): readonly BufferGeometry[] =>
    ([-1, 1] as const).map((side) =>
      place(new DodecahedronGeometry(headRadius * 0.18, 0), {
        x: side * headRadius * 0.17,
        y: -headRadius * 0.1,
        z: headRadius * 0.94,
        scaleX: 1.15,
        scaleY: 0.36,
        scaleZ: 0.32,
        rotateZ: side * -0.17,
      })
    );
  const stubble = place(new SphereGeometry(headRadius * 0.67, 8, 5), {
    y: -headRadius * 0.37,
    z: headRadius * 0.5,
    scaleX: 0.88,
    scaleY: 0.6,
    scaleZ: 0.4,
  });
  stubble.name = "facial-hair:stubble";
  const moustache = merge(moustacheParts(), "facial-hair:moustache");
  const shortBeard = merge([
    ...moustacheParts(),
    place(new DodecahedronGeometry(headRadius * 0.63, 1), {
      y: -headRadius * 0.43,
      z: headRadius * 0.34,
      scaleX: 0.82,
      scaleY: 0.72,
      scaleZ: 0.58,
    }),
  ], "facial-hair:short-beard");
  return { stubble, moustache, short_beard: shortBeard };
}

function createHairGeometry(
  headRadius: number,
): Readonly<Record<HairStyle, Readonly<Record<FacialHair, BufferGeometry>>>> {
  const hair = createBaseHairGeometry(headRadius);
  const facial = createFacialHairGeometry(headRadius);
  const styles = [
    "bald",
    "buzz_cut",
    "cropped",
    "undercut",
    "parted",
    "bob",
    "wavy",
    "ponytail",
    "bun",
  ] as const satisfies readonly HairStyle[];
  const facialStyles = [
    "none",
    "stubble",
    "moustache",
    "short_beard",
  ] as const satisfies readonly FacialHair[];
  const result = {} as Record<HairStyle, Record<FacialHair, BufferGeometry>>;
  for (const style of styles) {
    const byFacialHair = {} as Record<FacialHair, BufferGeometry>;
    for (const facialStyle of facialStyles) {
      const parts: BufferGeometry[] = [];
      if (style !== "bald") {
        parts.push(hair[style].clone());
      }
      if (facialStyle !== "none") {
        parts.push(facial[facialStyle].clone());
      }
      const geometry = parts.length === 0
        ? new BufferGeometry()
        : merge(parts, "hair:" + style + "+" + facialStyle);
      geometry.name = "hair:" + style + "+" + facialStyle;
      byFacialHair[facialStyle] = geometry;
    }
    result[style] = byFacialHair;
  }
  for (const geometry of Object.values(hair)) {
    geometry.dispose();
  }
  for (const geometry of Object.values(facial)) {
    geometry.dispose();
  }
  return result;
}

function createHeadwearGeometry(
  headRadius: number,
): Readonly<Record<VisibleHeadwear, BufferGeometry>> {
  const knitCap = merge([
    place(cap(headRadius * 1.08, Math.PI * 0.45, "knit-cap-dome"), {
      y: headRadius * 0.12,
    }),
    place(new TorusGeometry(headRadius * 0.91, headRadius * 0.075, 4, 10), {
      y: headRadius * 0.3,
      rotateX: Math.PI * 0.5,
    }),
    place(new DodecahedronGeometry(headRadius * 0.17, 0), {
      y: headRadius * 1.15,
    }),
  ], "headwear:knit_cap");
  const flatCap = merge([
    place(new SphereGeometry(headRadius * 1.03, 9, 5), {
      y: headRadius * 0.58,
      z: headRadius * 0.02,
      scaleY: 0.3,
      scaleZ: 1.08,
    }),
    place(new SphereGeometry(headRadius * 0.74, 8, 4), {
      y: headRadius * 0.3,
      z: headRadius * 0.74,
      scaleX: 1.22,
      scaleY: 0.12,
      scaleZ: 0.62,
    }),
  ], "headwear:flat_cap");
  const baseballCap = merge([
    place(cap(headRadius * 1.06, Math.PI * 0.45, "baseball-cap-crown"), {
      y: headRadius * 0.12,
      scaleY: 0.92,
    }),
    place(new SphereGeometry(headRadius * 0.72, 8, 4), {
      y: headRadius * 0.28,
      z: headRadius * 0.9,
      scaleX: 1.15,
      scaleY: 0.1,
      scaleZ: 0.78,
    }),
  ], "headwear:baseball_cap");
  const cycleHelmet = merge([
    place(cap(headRadius * 1.11, Math.PI * 0.43, "cycle-helmet-shell"), {
      y: headRadius * 0.14,
      scaleX: 1.02,
      scaleY: 0.9,
      scaleZ: 1.12,
    }),
    ...([-1, 1] as const).map((side) =>
      place(new BoxGeometry(
        headRadius * 0.13,
        headRadius * 0.08,
        headRadius * 0.55,
      ), {
        x: side * headRadius * 0.42,
        y: headRadius * 0.72,
        z: headRadius * 0.04,
        rotateX: -0.15,
      })
    ),
    place(new TorusGeometry(
      headRadius * 0.84,
      headRadius * 0.025,
      4,
      10,
      Math.PI * 0.72,
    ), {
      y: headRadius * 0.21,
      z: headRadius * 0.04,
      rotateX: Math.PI * 0.5,
      rotateZ: Math.PI * 0.14,
    }),
  ], "headwear:cycle_helmet");
  const hardHat = merge([
    place(cap(headRadius * 1.08, Math.PI * 0.41, "hard-hat-shell"), {
      y: headRadius * 0.17,
      scaleY: 0.9,
    }),
    place(new CylinderGeometry(
      headRadius * 1.13,
      headRadius * 1.13,
      headRadius * 0.08,
      12,
    ), { y: headRadius * 0.37, scaleZ: 1.13 }),
    place(new BoxGeometry(
      headRadius * 0.08,
      headRadius * 0.48,
      headRadius * 1.35,
    ), { y: headRadius * 0.66 }),
  ], "headwear:hard_hat");
  const beret = merge([
    place(new SphereGeometry(headRadius * 1.02, 10, 5), {
      x: -headRadius * 0.09,
      y: headRadius * 0.62,
      scaleX: 1.12,
      scaleY: 0.35,
      scaleZ: 1.08,
      rotateZ: -0.08,
    }),
    place(new CylinderGeometry(
      headRadius * 0.035,
      headRadius * 0.045,
      headRadius * 0.18,
      5,
    ), { y: headRadius * 0.99, rotateZ: -0.12 }),
  ], "headwear:beret");
  const headscarf = merge([
    place(cap(headRadius * 1.075, Math.PI * 0.45, "headscarf-crown"), {
      y: headRadius * 0.11,
      scaleZ: 1.04,
    }),
    ...([-1, 1] as const).map((side) =>
      place(new DodecahedronGeometry(headRadius * 0.36, 0), {
        x: side * headRadius * 0.82,
        y: -headRadius * 0.03,
        z: -headRadius * 0.27,
        scaleX: 0.44,
        scaleY: 1.05,
        scaleZ: 0.68,
      })
    ),
    place(new DodecahedronGeometry(headRadius * 0.25, 0), {
      y: -headRadius * 0.2,
      z: -headRadius * 1.02,
      scaleX: 1.15,
      scaleY: 0.85,
      scaleZ: 0.82,
    }),
    place(new ConeGeometry(headRadius * 0.18, headRadius * 0.72, 5), {
      x: -headRadius * 0.18,
      y: -headRadius * 0.48,
      z: -headRadius * 0.84,
      rotateZ: -0.22,
    }),
    place(new ConeGeometry(headRadius * 0.18, headRadius * 0.67, 5), {
      x: headRadius * 0.18,
      y: -headRadius * 0.45,
      z: -headRadius * 0.86,
      rotateZ: 0.25,
    }),
  ], "headwear:headscarf");
  return {
    knit_cap: knitCap,
    flat_cap: flatCap,
    baseball_cap: baseballCap,
    cycle_helmet: cycleHelmet,
    hard_hat: hardHat,
    beret,
    headscarf,
  };
}

function createNeckwearGeometry(): Readonly<
  Record<VisibleNeckwear, BufferGeometry>
> {
  const scarf = place(new TorusGeometry(0.195, 0.044, 5, 10), {
    rotateX: Math.PI * 0.5,
  });
  scarf.name = "neckwear:scarf";
  const kerchief = merge([
    place(polygon([
      { x: -0.2, y: 0.05 },
      { x: 0.2, y: 0.05 },
      { x: 0, y: -0.28 },
    ]), { z: 0.18 }),
    place(new TorusGeometry(0.185, 0.025, 4, 9), {
      y: 0.04,
      rotateX: Math.PI * 0.5,
    }),
  ], "neckwear:kerchief");
  const tie = merge([
    place(new DodecahedronGeometry(0.055, 0), {
      y: 0.02,
      z: 0.235,
      scaleX: 0.72,
      scaleY: 0.7,
      scaleZ: 0.42,
    }),
    place(polygon([
      { x: -0.045, y: 0.02 },
      { x: 0.045, y: 0.02 },
      { x: 0.032, y: -0.25 },
      { x: 0, y: -0.31 },
      { x: -0.032, y: -0.25 },
    ]), { y: -0.04, z: 0.235 }),
  ], "neckwear:tie");
  const chain = place(new TorusGeometry(0.15, 0.014, 4, 10), {
    y: -0.02,
    z: 0.04,
    scaleY: 1.18,
    rotateX: Math.PI * 0.5,
  });
  chain.name = "neckwear:chain";
  const lanyard = merge([
    place(polygon([
      { x: -0.1, y: 0.07 },
      { x: -0.035, y: -0.22 },
      { x: 0, y: -0.17 },
      { x: 0.035, y: -0.22 },
      { x: 0.1, y: 0.07 },
    ]), { z: 0.228 }),
    place(new BoxGeometry(0.105, 0.12, 0.018), {
      y: -0.27,
      z: 0.236,
    }),
  ], "neckwear:lanyard");
  return { scarf, kerchief, tie, lanyard, chain };
}

function createEyewearGeometry(): CharacterFeatureGeometrySet["eyewear"] {
  const round = new TorusGeometry(0.052, 0.008, 4, 8);
  round.name = "eyewear:round";
  const square = merge([
    place(new BoxGeometry(0.104, 0.012, 0.012), { y: 0.038 }),
    place(new BoxGeometry(0.104, 0.012, 0.012), { y: -0.038 }),
    place(new BoxGeometry(0.012, 0.076, 0.012), { x: -0.046 }),
    place(new BoxGeometry(0.012, 0.076, 0.012), { x: 0.046 }),
  ], "eyewear:square");
  return { round, square };
}

function createCarriedGeometry(): Readonly<
  Record<VisibleCarriedAccessory, BufferGeometry>
> {
  const satchel = merge([
    place(new BoxGeometry(0.31, 0.31, 0.13), { y: -0.03 }),
    place(new BoxGeometry(0.315, 0.09, 0.145), { y: 0.09, z: 0.015 }),
    place(new TorusGeometry(0.15, 0.016, 4, 9, Math.PI), {
      y: 0.18,
      rotateZ: Math.PI,
    }),
  ], "carried:satchel");
  const tote = merge([
    place(new CylinderGeometry(0.14, 0.18, 0.35, 4), { y: -0.05 }),
    ...([-1, 1] as const).map((side) =>
      place(new TorusGeometry(0.105, 0.012, 4, 8, Math.PI), {
        x: side * 0.055,
        y: 0.17,
        rotateZ: Math.PI,
      })
    ),
  ], "carried:tote");
  const backpack = merge([
    place(new BoxGeometry(0.36, 0.48, 0.18), {
      y: -0.02,
      scaleX: 0.94,
    }),
    place(new CylinderGeometry(0.16, 0.18, 0.14, 8), { y: 0.24 }),
    place(new BoxGeometry(0.24, 0.12, 0.04), { y: -0.08, z: -0.11 }),
  ], "carried:backpack");
  const briefcase = merge([
    place(new BoxGeometry(0.4, 0.27, 0.11), { y: -0.08 }),
    place(new TorusGeometry(0.09, 0.016, 4, 8, Math.PI), {
      y: 0.095,
      rotateZ: Math.PI,
    }),
    place(new BoxGeometry(0.34, 0.025, 0.118), { y: -0.02 }),
  ], "carried:briefcase");
  const shoppingBag = merge([
    place(new CylinderGeometry(0.13, 0.18, 0.34, 4), { y: -0.08 }),
    ...([-1, 1] as const).map((side) =>
      place(new TorusGeometry(0.09, 0.011, 4, 8, Math.PI), {
        x: side * 0.045,
        y: 0.12,
        rotateZ: Math.PI,
      })
    ),
  ], "carried:shopping_bag");
  const cane = merge([
    place(new CylinderGeometry(0.017, 0.019, 1.22, 6), { y: -0.02 }),
    place(new TorusGeometry(0.078, 0.017, 4, 9, Math.PI * 1.15), {
      x: 0.058,
      y: 0.59,
      rotateZ: Math.PI * 0.42,
    }),
  ], "carried:cane");
  const folio = merge([
    place(new BoxGeometry(0.3, 0.39, 0.065), { y: -0.04 }),
    place(new BoxGeometry(0.27, 0.018, 0.078), { y: 0.075 }),
    place(new BoxGeometry(0.045, 0.1, 0.08), { y: 0.19 }),
  ], "carried:folio");
  const camera = merge([
    place(new BoxGeometry(0.25, 0.17, 0.11)),
    place(new CylinderGeometry(0.065, 0.075, 0.09, 8), {
      z: 0.1,
      rotateX: Math.PI * 0.5,
    }),
    place(new TorusGeometry(0.19, 0.012, 4, 10, Math.PI), {
      y: 0.17,
      rotateZ: Math.PI,
    }),
  ], "carried:camera");
  const produceBasket = merge([
    place(new CylinderGeometry(0.2, 0.25, 0.24, 8), { y: -0.1 }),
    place(new TorusGeometry(0.2, 0.018, 4, 10, Math.PI), {
      y: 0.07,
      rotateZ: Math.PI,
    }),
    ...[-0.1, 0, 0.1].map((x) =>
      place(new SphereGeometry(0.065, 6, 4), {
        x,
        y: 0.055 + Math.abs(x) * 0.18,
        z: 0.01,
      })
    ),
  ], "carried:produce_basket");
  const toolRoll = merge([
    place(new CylinderGeometry(0.095, 0.095, 0.42, 7), {
      rotateZ: Math.PI * 0.5,
    }),
    place(new TorusGeometry(0.095, 0.014, 4, 8), {
      x: -0.08,
      rotateY: Math.PI * 0.5,
    }),
    place(new TorusGeometry(0.095, 0.014, 4, 8), {
      x: 0.08,
      rotateY: Math.PI * 0.5,
    }),
  ], "carried:tool_roll");
  const stroller = merge([
    ...([-1, 1] as const).flatMap((side) =>
      ([-1, 1] as const).map((end) =>
        place(new CylinderGeometry(0.075, 0.075, 0.045, 8), {
          x: side * 0.22,
          y: -0.33,
          z: end * 0.24,
          rotateZ: Math.PI * 0.5,
        })
      )
    ),
    place(new BoxGeometry(0.42, 0.25, 0.52), {
      y: -0.12,
      z: 0.02,
      rotateX: -0.16,
    }),
    place(new ConeGeometry(0.27, 0.28, 8, 1, true, 0, Math.PI), {
      y: 0.08,
      z: -0.04,
      rotateZ: Math.PI * 0.5,
    }),
    ...([-1, 1] as const).map((side) =>
      place(new CylinderGeometry(0.018, 0.018, 0.76, 5), {
        x: side * 0.17,
        y: 0.05,
        z: -0.16,
        rotateX: -0.48,
      })
    ),
    place(new CylinderGeometry(0.018, 0.018, 0.42, 5), {
      y: 0.4,
      z: -0.32,
      rotateZ: Math.PI * 0.5,
    }),
  ], "carried:stroller");
  const dogLead = merge([
    place(new DodecahedronGeometry(0.19, 0), {
      x: 0.33,
      y: -0.53,
      z: 0.06,
      scaleX: 1.08,
      scaleY: 0.72,
      scaleZ: 1.34,
    }),
    place(new DodecahedronGeometry(0.13, 0), {
      x: 0.34,
      y: -0.39,
      z: 0.18,
      scaleY: 1.08,
    }),
    ...([-1, 1] as const).flatMap((side) =>
      ([-1, 1] as const).map((end) =>
        place(new CylinderGeometry(0.025, 0.025, 0.28, 5), {
          x: 0.33 + side * 0.1,
          y: -0.71,
          z: 0.06 + end * 0.11,
        })
      )
    ),
    place(new ConeGeometry(0.055, 0.16, 5), {
      x: 0.27,
      y: -0.27,
      z: 0.19,
      rotateZ: -0.32,
    }),
    place(new ConeGeometry(0.055, 0.16, 5), {
      x: 0.41,
      y: -0.27,
      z: 0.19,
      rotateZ: 0.32,
    }),
    place(new CylinderGeometry(0.012, 0.012, 0.92, 5), {
      x: 0.16,
      y: -0.16,
      z: 0.11,
      rotateZ: -0.36,
    }),
  ], "carried:dog_lead");
  const waistPack = merge([
    place(new BoxGeometry(0.31, 0.14, 0.1), {
      y: -0.03,
      z: 0.02,
    }),
    place(new TorusGeometry(0.2, 0.014, 4, 10, Math.PI), {
      y: 0.04,
      z: -0.02,
      rotateZ: Math.PI,
    }),
  ], "carried:waist_pack");
  return {
    satchel,
    tote,
    backpack,
    briefcase,
    shopping_bag: shoppingBag,
    cane,
    folio,
    camera,
    produce_basket: produceBasket,
    tool_roll: toolRoll,
    stroller,
    dog_lead: dogLead,
    waist_pack: waistPack,
  };
}

export function createCharacterFeatureGeometrySet(
  headRadius: number,
): CharacterFeatureGeometrySet {
  return {
    hair: createHairGeometry(headRadius),
    headwear: createHeadwearGeometry(headRadius),
    neckwear: createNeckwearGeometry(),
    carried: createCarriedGeometry(),
    eyewear: createEyewearGeometry(),
  };
}

export function disposeCharacterFeatureGeometrySet(
  features: Readonly<CharacterFeatureGeometrySet>,
): void {
  const geometries = new Set<BufferGeometry>([
    ...Object.values(features.hair).flatMap((byFacialHair) =>
      Object.values(byFacialHair)
    ),
    ...Object.values(features.headwear),
    ...Object.values(features.neckwear),
    ...Object.values(features.carried),
    ...Object.values(features.eyewear),
  ]);
  for (const geometry of geometries) {
    geometry.dispose();
  }
}
